import { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Archive, ArrowCounterClockwise, Warning } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

export default function ArchivedItemsPage() {
  const { user } = useAuth();
  const canRestore = user?.role === 'admin' || user?.role === 'owner';

  const [collections, setCollections] = useState([]);
  const [active, setActive] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState({});

  const loadCollections = useCallback(async () => {
    try {
      const res = await api.get('/archived/collections');
      setCollections(res.data);
      if (res.data.length && !active) {
        const first = res.data.find((c) => c.archived_count > 0) || res.data[0];
        setActive(first.collection);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load archived summary');
    }
  }, [active]);

  const loadItems = useCallback(async (collection) => {
    if (!collection) return;
    setLoading(true);
    try {
      const res = await api.get(`/archived/${collection}`);
      setItems(res.data.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load items');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCollections();
  }, [loadCollections]);

  useEffect(() => {
    if (active) loadItems(active);
  }, [active, loadItems]);

  const handleRestore = async (itemId) => {
    if (!canRestore) return;
    if (!window.confirm('Yeh item wapas restore kar di jaye? Phir se list mein aa jaayegi.')) return;
    setRestoring((p) => ({ ...p, [itemId]: true }));
    try {
      await api.post(`/archived/${active}/${itemId}/restore`);
      toast.success('Restored successfully');
      setItems((prev) => prev.filter((i) => i.id !== itemId));
      setCollections((prev) =>
        prev.map((c) =>
          c.collection === active ? { ...c, archived_count: Math.max(0, c.archived_count - 1) } : c
        )
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Restore failed');
    } finally {
      setRestoring((p) => {
        const n = { ...p };
        delete n[itemId];
        return n;
      });
    }
  };

  const fmtDate = (s) => {
    if (!s) return '—';
    try {
      return new Date(s).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return s;
    }
  };

  const activeLabel = collections.find((c) => c.collection === active)?.label || '';

  return (
    <div className="space-y-6" data-testid="archived-items-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Archive size={28} weight="duotone" className="text-amber-600" />
            Data Retention — Archived Items
          </h1>
          <p className="text-sm text-gray-600 mt-1 max-w-2xl">
            Saara "deleted" data yahan preserved hai. Koi bhi item galti se delete ho gaya ho to
            Admin/Owner ek click mein restore kar sakte hain.
          </p>
        </div>
      </div>

      {/* Info banner */}
      {!canRestore && (
        <div className="flex gap-2 items-start bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
          <Warning size={18} weight="bold" className="mt-0.5 shrink-0" />
          <span>Aap view-only access rakhte hain. Restore karne ke liye Admin/Owner se contact karein.</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
        {/* Collections sidebar */}
        <aside className="bg-white rounded-xl border border-gray-200 overflow-hidden self-start">
          <div className="px-4 py-3 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Collections
          </div>
          <div className="max-h-[70vh] overflow-y-auto" data-testid="archived-collections-list">
            {collections.length === 0 && (
              <div className="p-4 text-sm text-gray-500">Loading...</div>
            )}
            {collections.map((c) => {
              const isActive = c.collection === active;
              return (
                <button
                  key={c.collection}
                  onClick={() => setActive(c.collection)}
                  data-testid={`archived-collection-${c.collection}`}
                  className={`w-full text-left px-4 py-2.5 border-b border-gray-50 flex items-center justify-between gap-2 transition-colors ${
                    isActive
                      ? 'bg-amber-50 text-amber-900 font-semibold'
                      : 'hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  <span className="text-sm truncate">{c.label}</span>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${
                      c.archived_count > 0
                        ? 'bg-amber-200 text-amber-900'
                        : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {c.archived_count}
                  </span>
                </button>
              );
            })}
          </div>
        </aside>

        {/* Items panel */}
        <section className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <div>
              <div className="text-xs text-gray-500">Archived in</div>
              <div className="text-sm font-semibold text-gray-900">{activeLabel || '—'}</div>
            </div>
            <div className="text-xs text-gray-500">{items.length} items</div>
          </div>

          {loading ? (
            <div className="p-8 text-center text-sm text-gray-500">Loading items...</div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center text-sm text-gray-500" data-testid="archived-empty-state">
              <Archive size={40} weight="duotone" className="mx-auto text-gray-300 mb-2" />
              Koi archived item nahi mila.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-2.5 text-left">Identifier</th>
                    <th className="px-4 py-2.5 text-left">Details</th>
                    <th className="px-4 py-2.5 text-left whitespace-nowrap">Deleted At</th>
                    <th className="px-4 py-2.5 text-left">Deleted By</th>
                    <th className="px-4 py-2.5 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr
                      key={it.id}
                      className="border-t border-gray-100 hover:bg-amber-50/40"
                      data-testid={`archived-item-row-${it.id}`}
                    >
                      <td className="px-4 py-3 font-medium text-gray-900 break-all max-w-[220px]">
                        {it.primary_value}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600">
                        <div className="flex flex-wrap gap-x-3 gap-y-1">
                          {Object.entries(it.preview || {})
                            .filter(([, v]) => v !== null && v !== undefined && v !== '')
                            .map(([k, v]) => (
                              <span key={k}>
                                <span className="text-gray-400">{k}:</span>{' '}
                                <span className="text-gray-800">{String(v)}</span>
                              </span>
                            ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">
                        {fmtDate(it.deleted_at)}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600">
                        {it.deleted_by_name || '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {canRestore && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={!!restoring[it.id]}
                            onClick={() => handleRestore(it.id)}
                            data-testid={`archived-restore-btn-${it.id}`}
                            className="h-8 gap-1.5"
                          >
                            <ArrowCounterClockwise size={14} weight="bold" />
                            {restoring[it.id] ? 'Restoring...' : 'Restore'}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
